import React, { useEffect, useMemo, useRef } from 'react';
import './BottomDockedSheet.css';

/**
 * BottomDockedSheet
 * - Always docked to bottom of its relatively positioned parent (e.g., map wrapper)
 * - Controlled via isExpanded; supports Esc and outside click to collapse
 * - Emits height changes so parent can adjust map padding if needed
 */
const BottomDockedSheet = ({
  isExpanded,
  onToggle,
  onCollapse,
  collapsedHeight = 80,
  expandedHeight = 300,
  title,
  subtitle,
  onPrimary,
  primaryLabel = 'Directions',
  children,
  ariaLabel = 'Location details',
  onHeightChange
}) => {
  const rootRef = useRef(null);
  const containerRef = useRef(null);

  const targetHeight = useMemo(
    () => (isExpanded ? expandedHeight : collapsedHeight),
    [isExpanded, collapsedHeight, expandedHeight]
  );

  useEffect(() => {
    if (!containerRef.current) return;
    containerRef.current.style.height = `${targetHeight}px`;
    if (typeof onHeightChange === 'function') {
      onHeightChange(targetHeight);
    }
  }, [targetHeight, onHeightChange]);

  // Esc to collapse
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        onCollapse?.();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCollapse]);

  // Outside click to collapse
  useEffect(() => {
    const handleClick = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) {
        onCollapse?.();
      }
    };
    // Attach on capture to intercept before map
    document.addEventListener('mousedown', handleClick, true);
    return () => document.removeEventListener('mousedown', handleClick, true);
  }, [onCollapse]);

  return (
    <div
      ref={rootRef}
      className="bottom-docked-sheet"
      role="region"
      aria-label={ariaLabel}
      aria-expanded={isExpanded}
      style={{
        transform: 'translateZ(0)'
      }}
    >
      <div
        ref={containerRef}
        className="bottom-docked-sheet__container"
      >
        <button
          type="button"
          className="bottom-docked-sheet__handle"
          aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
          onClick={onToggle}
          style={{ display: 'block', background: 'transparent', border: 'none', cursor: 'pointer' }}
        />
        <div
          className="bottom-docked-sheet__header"
          onClick={onToggle}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onToggle?.();
            }
          }}
        >
          <div className="bottom-docked-sheet__title">
            <h4>{title}</h4>
            {subtitle && <div className="bottom-docked-sheet__subtitle">{subtitle}</div>}
          </div>
          <div className="bottom-docked-sheet__primary">
            {onPrimary && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onPrimary();
                }}
              >
                {primaryLabel}
              </button>
            )}
          </div>
        </div>
        {isExpanded && (
          <div className="bottom-docked-sheet__content">
            {children}
          </div>
        )}
      </div>
    </div>
  );
};

export default BottomDockedSheet;


